import React, { useCallback } from "react";
import { Start } from "./steps/start";
import { Guest, IContractModel, IReservation } from "./interfaces";
import { ContainerBodyWebCheckin, Content } from "./style";
import { ReservationSteps } from "./steps/reservationSteps";
import { Finish } from "./steps/finish";
import { useSearchParams } from "react-router-dom";
import { useApiWebCheckinUser } from "../../../hooks/WebCheckinUsers/api";
import { useTheme } from "../../../hooks/WebCheckinUsers/theme";
import { StepReservationSteps, useGlobalWebCheckinUser } from "../../../hooks/WebCheckinUsers/global";
import { MyCircularProgress } from "../../../components/MyCircularProgress";
import { IconPowered } from "../../../components/SvgComponents/Icons/IconPowered";

export const WebCheckIn: React.FC = () => {
  const [step, setStep] = React.useState<
    "start" | "reservation-steps" | "finish"
  >("start");

  const [params] = useSearchParams();

  const { api } = useApiWebCheckinUser();
  const { logo, colorsTheme } = useTheme();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [controlRender, setControlRender] = React.useState<boolean>(false);

  const { theme, containerGlobalRef, notify, setContractModel, contractModel, actualReservationStep } = useGlobalWebCheckinUser();

  const [reservation, setReservation] = React.useState<IReservation | null>(
    null
  );

  const [passwords, setPasswords] = React.useState<{
    offline_password: string | null;
    online_password: string | null;
  }>({
    offline_password: null,

    online_password: null,
  });

  const getLocalization = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(`${position.coords.latitude},${position.coords.longitude}`);
        },
        () => {
          resolve('');
        }
      );
    });
  }, []);

  const getIp = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      fetch('https://api.ipify.org?format=json')
        .then(res => res.json())
        .then(data => {
          resolve(data?.ip);
        })
        .catch(err => {
          console.error('Erro ao obter IP:', err);
          return null;
        });
    });
  }, []);

  const handleSave = useCallback(
    async (guests: Guest[], contract?: IContractModel) => {
      setLoading(true);
      try {
        const correct_guests = guests.map((guest) => {
          return {
            ...guest,
            birth: new Date(guest.birth + "T03:00:00.000Z"),
            document_type: guest.cpf ? "cpf" : "passport",
            document_value: guest.cpf ? guest.cpf : guest.passport,
            document_image_back: guest.document_file_back,
            document_image_front: guest.document_file,
            selfie_image: guest.selfie_file,
            compare_valid: guest.compare_valid,
            compare_score: guest.compare_score
          };
        });


        let localization = "";
        let device = "";
        let ip = "";

        if (contract?.signature) {
          localization = await getLocalization();
          device = navigator.userAgent;
          ip = await getIp();
        }

        const { data } = await api.post("/check-ins", {
          reservation_stays_id: reservation?.id,
          guests: correct_guests,
          contract_model: contract,
          localization: localization,
          device: device,
          device_ip: ip,
          location: localization
        });

        setPasswords(data);

        notify("Check-in realizado com sucesso!", "success");
        setStep("finish");
      } catch (e) {
        setLoading(false);
        notify(
          "Problema ao realizar o check-in! Tente novamente mais tarde",
          "alert"
        );
      }
      setLoading(false);
    },
    [reservation, contractModel, getLocalization]
  );

  const StartChildren = (
    <Start
      onSave={(reservation, variables) => {
        const correctReservation = reservation as IReservation;
        correctReservation.guests = correctReservation.guests.sort((a, b) =>
          a.primary ? -1 : 1
        );
        setReservation(correctReservation);
        setContractModel(reservation?.contract_model as IContractModel, variables);
        setStep("reservation-steps");
      }}
      defaultReservationId={params.get("reservation_id") ?? ""}
      defaultCheckInDate={params.get("date") ?? ""}
    />
  );

  return (
    <ContainerBodyWebCheckin padding={actualReservationStep === StepReservationSteps.SignContract ? "20px 10px" : "20px"}>
      <div style={{ marginTop: 30, marginBottom: 10 }}>
        <img src={logo} style={{
          width: '100%',
          maxHeight: '60px',
          height: 'auto',
          objectFit: 'contain'
        }}
        />
      </div>

      {step !== "reservation-steps" && (
        <Content
          ref={containerGlobalRef}
          style={{
            position: "relative",
            overflow: "hidden",
            display: loading ? "none" : "flex",
          }}
        >
          {step === "start" && !controlRender && StartChildren}
          {step === "start" && controlRender && StartChildren}
          {step === "finish" && (
            <Finish reservation={reservation as IReservation} {...passwords} />
          )}
        </Content>
      )}

      {step === "reservation-steps" && (
        <ReservationSteps
          containerGlobalRef={containerGlobalRef}
          loading={loading}
          reservation={reservation as any}
          onSave={handleSave}
        />
      )}

      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <MyCircularProgress size={30} color={colorsTheme.secondary.light} />
        </div>
      )
      }

      <a href="https://webcheckin.app/" target="_blank" rel="noopener noreferrer">
        <IconPowered theme={theme} />
      </a>
    </ContainerBodyWebCheckin >
  );
};
